/**
 * Per-schedule optimize settings: the form model of a schedule's own
 * optimize configuration, and its conversion to and from the optimize
 * request body.
 *
 * Encoding uses the same builder and defaults as the Optimize tab
 * (optimizeRequestBody.js), so a schedule sends exactly what a manual
 * Optimize with the same parameters would. Scheduled runs never pin an
 * iperf run (optimize picks the best recent one), never debug, and get
 * their steps from the schedule.
 */
import { OPTIMIZE_DEFAULTS as D, buildOptimizeRequestBody } from './optimizeRequestBody';

/**
 * Scopes offered for a schedule. 'devices' (devices picked on the map in
 * the Optimize tab) is not offered for new schedules; the dialog shows it
 * only for a schedule that already has one, so it is kept, never widened.
 */
export const SCHEDULE_SCOPE_OPTIONS = [
  { value: 'site', label: 'Whole site' },
  { value: 'caps', label: 'Selected CAPs (and their trees)' },
  { value: 'trees', label: 'Custom trees' },
];

/**
 * Parameter schema: `key` is the form field (an OPTIMIZE_DEFAULTS key) and
 * `field` the request field it is sent as. `inverse` marks a field sent as
 * the negation of the form value.
 */
export const SCHEDULE_PARAM_GROUPS = [
  {
    id: 'general',
    label: 'General',
    params: [
      { key: 'maxIterations', field: 'max_iterations', label: 'Max iterations', type: 'number', default: D.maxIterations, min: 1, max: 10, step: 1, help: 'Topology → power → channels rounds before stopping.' },
      { key: 'useIperf', field: 'use_iperf', label: 'Use iperf measurements', type: 'bool', default: D.useIperf, help: 'Anchor link costs on the best-matching recent iperf run, if any.' },
      { key: 'useHealthFocus', field: 'use_health_focus', label: 'Focus on unhealthy devices', type: 'bool', default: D.useHealthFocus },
      { key: 'rebuildTree', field: 'rebuild_tree', label: 'Rebuild tree from scratch', type: 'bool', default: D.rebuildTree, help: 'Plan the whole tree anew instead of improving the current one. Large changes.' },
    ],
  },
  {
    id: 'topology',
    label: 'Topology',
    params: [
      { key: 'improvementFloor', field: 'improvement_floor', label: 'Improvement floor', type: 'number', default: D.improvementFloor, min: 0.01, step: 0.1, positive: true, help: 'Minimum cost gain for a parent move.' },
      { key: 'reparentWhitelistCount', field: 'reparent_whitelist_count', label: 'Parents in whitelist', type: 'number', default: D.reparentWhitelistCount, min: 1, max: 5, step: 1, help: 'Ordered parents written per moved lamp (primary + alternates).' },
    ],
  },
  {
    id: 'power',
    label: 'Power',
    params: [
      { key: 'powerImprovementFloorDb', field: 'power_improvement_floor_db', label: 'Power improvement floor (dB)', type: 'number', default: D.powerImprovementFloorDb, min: 0, step: 0.5 },
      { key: 'powerUplinkSeverityWeight', field: 'power_uplink_severity_weight', label: 'Uplink severity weight', type: 'number', default: D.powerUplinkSeverityWeight, min: 0.1, step: 0.1, positive: true },
      {
        key: 'powerRegulatoryDomain', field: 'power_regulatory_domain', label: 'Power regulatory domain', type: 'select', default: D.powerRegulatoryDomain,
        options: [
          { value: 'default', label: 'Default (hardware cap only)' },
          { value: 'us', label: 'US — 36 dBm EIRP' },
          { value: 'eu', label: 'EU — 23 dBm EIRP' },
          { value: 'il', label: 'IL — 23 dBm EIRP' },
        ],
      },
      { key: 'powerCapAntennaGainDbi', field: 'power_cap_antenna_gain_dbi', label: 'CAP antenna gain (dBi)', type: 'number', default: D.powerCapAntennaGainDbi, step: 0.5 },
      { key: 'powerNodeAntennaGainDbi', field: 'power_node_antenna_gain_dbi', label: 'Lamp antenna gain (dBi)', type: 'number', default: D.powerNodeAntennaGainDbi, step: 0.5 },
    ],
  },
  {
    id: 'channels',
    label: 'Channels',
    params: [
      {
        key: 'regulatoryDomain', field: 'regulatory_domain', label: 'DFS handling', type: 'select', default: D.regulatoryDomain,
        options: [
          { value: 'default', label: 'Default' },
          { value: 'dfs_allowed', label: 'DFS allowed' },
          { value: 'dfs_blocked', label: 'DFS blocked' },
        ],
      },
      { key: 'capChannelBandwidth', field: 'cap_channel_bandwidth', label: 'CAP planning width (MHz)', type: 'select', default: D.capChannelBandwidth, options: [20, 40, 80].map((v) => ({ value: v, label: `${v}` })) },
      { key: 'hopChannelBandwidth', field: 'hop_channel_bandwidth', label: 'Lamp planning width (MHz)', type: 'select', default: D.hopChannelBandwidth, options: [20, 40, 80].map((v) => ({ value: v, label: `${v}` })) },
      { key: 'minFrequencySeparationMHz', field: 'min_frequency_separation_mhz', label: 'Min gap, APs that hear each other (MHz)', type: 'number', default: D.minFrequencySeparationMHz, min: 0, step: 20 },
      { key: 'capSameChassisMinSep', field: 'cap_same_chassis_min_frequency_separation_mhz', label: 'CAP radios min gap (MHz)', type: 'number', default: D.capSameChassisMinSep, min: 0, step: 20 },
      { key: 'hopSameChassisMinSep', field: 'hop_same_chassis_min_frequency_separation_mhz', label: 'Lamp radios min gap (MHz)', type: 'number', default: D.hopSameChassisMinSep, min: 0, step: 20 },
      { key: 'threshold', field: 'threshold', label: 'Conflict threshold', type: 'number', default: D.threshold, min: 0, max: 1, step: 0.05 },
      { key: 'channelGlobalAcceptFloor', field: 'channel_global_accept_floor', label: 'Min total conflict reduction', type: 'number', default: D.channelGlobalAcceptFloor, min: 0, max: 1, step: 0.01 },
      { key: 'channelImprovementFloor', field: 'channel_improvement_floor', label: 'Per-move improvement floor', type: 'number', default: D.channelImprovementFloor, min: 0, step: 0.01 },
      { key: 'channelStickyCooldownMin', field: 'channel_stickiness_cooldown_minutes', label: 'Recently moved cooldown (min)', type: 'number', default: D.channelStickyCooldownMin, min: 0, step: 10 },
      { key: 'neighborRssiFloorDbm', field: 'neighbor_rssi_floor_dbm', label: 'Heard-neighbour floor (dBm)', type: 'number', default: D.neighborRssiFloorDbm, max: 0, step: 1 },
      { key: 'capOnlyActions', field: 'cap_only_actions', label: 'Change CAP channels only', type: 'bool', default: D.capOnlyActions },
      { key: 'excludeIdleAPs', field: 'idle_ap_retry_disabled', label: 'Drop idle APs when stuck', type: 'bool', default: D.excludeIdleAPs, inverse: true },
      { key: 'fixChannelGroups', field: 'fix_channel_groups', label: 'Lock channel groups', type: 'bool', default: D.fixChannelGroups },
      { key: 'segmentAware', field: 'segment_aware', label: 'Respect segments', type: 'bool', default: D.segmentAware },
      { key: 'excludePublicWifiRadios', field: 'exclude_public_wifi_radios', label: 'Skip Public WiFi radios', type: 'bool', default: D.excludePublicWifiRadios },
      { key: 'excludeMaxChildrenZeroRadios', field: 'exclude_max_children_zero_radios', label: 'Skip radios with mesh disabled', type: 'bool', default: D.excludeMaxChildrenZeroRadios },
    ],
  },
];

const ALL_PARAMS = SCHEDULE_PARAM_GROUPS.flatMap((g) => g.params);

/** Form keys of every parameter, for tests that compare with the Optimize tab. */
export const SCHEDULE_PARAM_KEYS = ALL_PARAMS.map((p) => p.key);

/** Default form values (scope + every parameter). */
export function defaultScheduleSettings() {
  const out = {
    scopeMode: 'site',
    capSerials: [],
    customTreeIds: [],
    focusSerials: [],
    channelGroupIds: [],
    channelAllowList5GHz: '',
    channelAllowList6GHz: '',
  };
  ALL_PARAMS.forEach((p) => { out[p.key] = p.default; });
  return out;
}

/** Number of parameters that differ from their defaults, per group id. */
export function changedParamCounts(form) {
  const counts = {};
  SCHEDULE_PARAM_GROUPS.forEach((g) => {
    counts[g.id] = g.params.filter((p) => form?.[p.key] !== p.default).length;
  });
  if (form && ((form.channelGroupIds || []).length > 0 || String(form.channelAllowList5GHz || '').trim() || String(form.channelAllowList6GHz || '').trim())) {
    counts.channels += 1;
  }
  return counts;
}

/** First invalid number parameter as a message, or '' when all are valid. */
export function invalidParamMessage(form) {
  const bad = ALL_PARAMS.filter((p) => p.type === 'number').find((p) => {
    const v = form?.[p.key];
    return v === '' || v === null || !Number.isFinite(Number(v))
      || (p.min !== undefined && v < p.min) || (p.max !== undefined && v > p.max)
      || (p.positive && v <= 0);
  });
  if (!bad) return '';
  const range = [bad.min !== undefined ? `≥ ${bad.min}` : '', bad.max !== undefined ? `≤ ${bad.max}` : ''].filter(Boolean).join(' and ');
  return `${bad.label}: enter a number${range ? ` ${range}` : ''}.`;
}

/**
 * Encode the form into the optimize request body. Returns { body } or
 * { error } (scope, number or channel list problems).
 */
export function settingsToOptimizeRequest(form) {
  const f = { ...defaultScheduleSettings(), ...(form || {}) };
  if (f.scopeMode === 'caps' && f.capSerials.length === 0) return { error: 'Select at least one CAP for the scope.' };
  if (f.scopeMode === 'trees' && f.customTreeIds.length === 0) return { error: 'Select at least one custom tree for the scope.' };
  // An empty device selection would mean the whole site; never widen silently.
  if (f.scopeMode === 'devices' && f.focusSerials.length === 0) return { error: 'Select at least one device for the scope, or choose another scope.' };
  const invalid = invalidParamMessage(f);
  if (invalid) return { error: invalid };

  const params = {};
  ALL_PARAMS.forEach((p) => { params[p.key] = p.type === 'number' ? Number(f[p.key]) : f[p.key]; });
  const built = buildOptimizeRequestBody({
    ...params,
    scopeMode: f.scopeMode,
    selectedCapSerials: f.capSerials,
    selectedCustomTreeIds: f.customTreeIds,
    mapFocusSerials: f.focusSerials,
    channelGroupIds: f.channelGroupIds,
    channelAllowList5GHz: f.channelAllowList5GHz,
    channelAllowList6GHz: f.channelAllowList6GHz,
  });
  if (built.error) return { error: built.error };
  const body = { ...built.body };
  // Per run, not per schedule: steps come from the schedule, optimize picks
  // the iperf run, and scheduled runs never debug.
  delete body.enabled_steps;
  delete body.iperf_run_id;
  delete body.debug;
  return { body };
}

/** Decode a stored optimize request back into form values (for editing). */
export function optimizeRequestToSettings(req) {
  const r = req || {};
  const f = defaultScheduleSettings();
  ALL_PARAMS.forEach((p) => {
    if (!(p.field in r)) return;
    f[p.key] = p.inverse ? !r[p.field] : r[p.field];
  });
  if (Array.isArray(r.channel_group_ids)) f.channelGroupIds = [...r.channel_group_ids];
  if (r.channel_allow_lists) {
    f.channelAllowList5GHz = (r.channel_allow_lists['5GHz'] || []).join(', ');
    f.channelAllowList6GHz = (r.channel_allow_lists['6GHz'] || []).join(', ');
  }
  // Same precedence as the request is built with (one scope per request).
  if (Array.isArray(r.cap_serials) && r.cap_serials.length > 0) {
    f.scopeMode = 'caps';
    f.capSerials = [...r.cap_serials];
  } else if (Array.isArray(r.focus_serials) && r.focus_serials.length > 0) {
    f.scopeMode = 'devices';
    f.focusSerials = [...r.focus_serials];
  } else if (r.custom_tree_id || (Array.isArray(r.custom_tree_ids) && r.custom_tree_ids.length > 0)) {
    f.scopeMode = 'trees';
    f.customTreeIds = r.custom_tree_id ? [r.custom_tree_id] : [...r.custom_tree_ids];
  }
  return f;
}
